import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ForceGraph2D from "react-force-graph-2d";
import Navigation from "@/components/Navigation";
import {
  Network, Search, X, Users, GitBranch, Landmark, Loader2,
  ArrowUpRight, Crown, Link2, Info, Maximize2, ZoomIn, ZoomOut, Sparkles,
  Map as MapIcon, Grid3X3, GitCompare, Bookmark, Trash2,
} from "lucide-react";
import { IdeologyMap } from "@/components/backroom/IdeologyMap";
import { HeatmapView } from "@/components/backroom/HeatmapView";
import { CompareView } from "@/components/backroom/CompareView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_COLOR = { D: "#3b82f6", R: "#ef4444", I: "#a855f7" };
const PARTY_NAME = { D: "Democrat", R: "Republican", I: "Independent" };
const COMMUNITY_PALETTE = [
  "#f97316", "#3b82f6", "#10b981", "#eab308", "#ec4899", "#8b5cf6",
  "#14b8a6", "#ef4444", "#84cc16", "#06b6d4", "#f59e0b", "#6366f1",
];
const SIGNALS = [
  { id: "votes", label: "Voting alignment", icon: GitBranch, blurb: "Members who vote the same way on roll calls" },
  { id: "cosponsor", label: "Co-sponsorship", icon: Users, blurb: "Members who sign onto the same bills" },
  { id: "committee", label: "Committees", icon: Landmark, blurb: "Members who serve on the same committees" },
];
const SIZE_METRICS = [
  { id: "degree", label: "Connections", blurb: "How many strong ties a member has" },
  { id: "betweenness", label: "Bridge power", blurb: "How often they connect otherwise-separate groups" },
  { id: "eigenvector", label: "Influence", blurb: "Connected to other well-connected members" },
];

const rootCommittee = (c) => (c || "").split(", ").pop().split("→")[0].trim();

const VIEWS = [
  { id: "network", label: "Network", icon: Network },
  { id: "ideology", label: "Ideology Map", icon: MapIcon },
  { id: "heatmap", label: "Heatmap", icon: Grid3X3 },
  { id: "compare", label: "Compare Congresses", icon: GitCompare },
];

export default function Factions() {
  const [view, setView] = useState("network");
  const [signal, setSignal] = useState("votes");
  const [chamber, setChamber] = useState("");
  const [congress, setCongress] = useState(119);
  const [colorBy, setColorBy] = useState("party");
  const [sizeBy, setSizeBy] = useState("degree");
  const [minWeight, setMinWeight] = useState(null);
  const [partyFilter, setPartyFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [committeeFilter, setCommitteeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [policyArea, setPolicyArea] = useState("");
  const [policyAreas, setPolicyAreas] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [data, setData] = useState(null);
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [focusMode, setFocusMode] = useState(0); // 0 off · 1 direct ties · 2 extended
  const [edgeDetail, setEdgeDetail] = useState(null);
  const [edgeLoading, setEdgeLoading] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [showViews, setShowViews] = useState(false);
  const [viewName, setViewName] = useState("");
  const [savingView, setSavingView] = useState(false);
  const [viewError, setViewError] = useState(null);
  const pendingMinWeightRef = useRef(null);
  const graphRef = useRef();
  const wrapRef = useRef();
  const pageWrapRef = useRef();
  const [dims, setDims] = useState({ w: 800, h: 640 });
  const pollRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) {
        setDims({ w: wrapRef.current.clientWidth, h: Math.max(560, window.innerHeight - 320) });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const fetchGraph = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    setEdgeDetail(null);
    setBriefing(null);
    try {
      const params = { signal, congress };
      if (chamber) params.chamber = chamber;
      if (policyArea && signal !== "committee") params.policy_area = policyArea;
      const r = await axios.get(`${API}/network/graph`, { params });
      if (reqIdRef.current !== myReq) return;
      if (r.status === 202 || r.data?.status === "building") {
        setBuilding(r.data);
        setData(null);
        if (!pollRef.current) pollRef.current = setTimeout(() => { pollRef.current = null; fetchGraph(); }, 10000);
      } else {
        setBuilding(null);
        setData(r.data);
        if (pendingMinWeightRef.current != null) {
          setMinWeight(pendingMinWeightRef.current);
          pendingMinWeightRef.current = null;
        } else {
          setMinWeight(r.data?.meta?.minWeight ?? null);
        }
      }
    } catch (e) {
      if (reqIdRef.current !== myReq) return;
      if (e?.response?.status === 202) {
        setBuilding(e.response.data);
        if (!pollRef.current) pollRef.current = setTimeout(() => { pollRef.current = null; fetchGraph(); }, 10000);
      } else {
        setError(e?.response?.data?.message || "Unable to load the network, try again shortly.");
      }
    } finally {
      if (reqIdRef.current === myReq) setLoading(false);
    }
  }, [signal, chamber, congress, policyArea]);

  useEffect(() => { fetchGraph(); return () => pollRef.current && clearTimeout(pollRef.current); }, [fetchGraph]);

  useEffect(() => {
    axios.get(`${API}/network/policy-areas`).then((r) => setPolicyAreas(r.data?.areas || [])).catch(() => {});
  }, []);

  const fetchBriefing = async () => {
    setBriefingLoading(true);
    try {
      const params = { signal, congress };
      if (chamber) params.chamber = chamber;
      if (policyArea && signal !== "committee") params.policy_area = policyArea;
      const r = await axios.get(`${API}/network/briefing`, { params });
      setBriefing(r.data?.findings || []);
    } catch {
      setBriefing([{ title: "Briefing unavailable", detail: "Could not generate the briefing, try again shortly." }]);
    } finally {
      setBriefingLoading(false);
    }
  };

  const loadSavedViews = useCallback(async () => {
    try { const r = await axios.get(`${API}/network/views`); setSavedViews(r.data || []); } catch { /* not critical */ }
  }, []);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  const saveCurrentView = async () => {
    if (!viewName.trim() || savingView) return;
    setSavingView(true);
    setViewError(null);
    try {
      const r = await axios.post(`${API}/network/views`, {
        name: viewName.trim(),
        config: { view, signal, chamber, congress, policyArea, colorBy, sizeBy, partyFilter, stateFilter, committeeFilter, minWeight },
      });
      setSavedViews((v) => [r.data, ...v]);
      setViewName("");
    } catch (e) {
      setViewError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not save, try again.");
    }
    finally { setSavingView(false); }
  };

  const applySavedView = (cfg) => {
    if (cfg.minWeight != null) { pendingMinWeightRef.current = cfg.minWeight; setMinWeight(cfg.minWeight); }
    setView(cfg.view || "network");
    setSignal(cfg.signal ?? "votes");
    setChamber(cfg.chamber ?? "");
    setCongress(cfg.congress ?? 119);
    setPolicyArea(cfg.policyArea ?? "");
    setColorBy(cfg.colorBy ?? "party");
    setSizeBy(cfg.sizeBy ?? "degree");
    setPartyFilter(cfg.partyFilter ?? "all");
    setStateFilter(cfg.stateFilter ?? "all");
    setCommitteeFilter(cfg.committeeFilter ?? "all");
    setShowViews(false);
  };

  const deleteSavedView = async (id) => {
    try {
      await axios.delete(`${API}/network/views/${id}`);
      setSavedViews((v) => v.filter((x) => x.view_id !== id));
    } catch { /* not critical */ }
  };

  // ---- client-side filtering -------------------------------------------
  const allStates = useMemo(() => {
    const s = new Set((data?.nodes || []).map((n) => n.state).filter(Boolean));
    return Array.from(s).sort();
  }, [data]);
  const allCommittees = useMemo(() => {
    const s = new Set();
    (data?.nodes || []).forEach((n) => (n.committees || []).forEach((c) => s.add(rootCommittee(c))));
    return Array.from(s).sort();
  }, [data]);

  const graphData = useMemo(() => {
    if (!data) return { nodes: [], links: [] };
    let nodes = data.nodes;
    if (partyFilter !== "all") nodes = nodes.filter((n) => n.party === partyFilter);
    if (stateFilter !== "all") nodes = nodes.filter((n) => n.state === stateFilter);
    if (committeeFilter !== "all")
      nodes = nodes.filter((n) => (n.committees || []).some((c) => rootCommittee(c) === committeeFilter));
    const keep = new Set(nodes.map((n) => n.id));
    const links = data.links
      .filter((l) => minWeight == null || l.weight >= minWeight)
      .filter((l) => keep.has(typeof l.source === "object" ? l.source.id : l.source) &&
                     keep.has(typeof l.target === "object" ? l.target.id : l.target))
      .map((l) => ({ ...l }));
    return { nodes: nodes.map((n) => ({ ...n })), links };
  }, [data, partyFilter, stateFilter, committeeFilter, minWeight]);

  // search highlight
  const highlight = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !data) return null;
    const hits = new Set(graphData.nodes.filter((n) => n.name.toLowerCase().includes(q)).map((n) => n.id));
    if (hits.size === 0) return { hits, neigh: new Set() };
    const neigh = new Set();
    graphData.links.forEach((l) => {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      if (hits.has(s)) neigh.add(t);
      if (hits.has(t)) neigh.add(s);
    });
    return { hits, neigh };
  }, [search, graphData, data]);

  // adjacency + focus sets (1-hop / 2-hop from the selected node)
  const focusSets = useMemo(() => {
    if (!selectedNode || focusMode === 0) return null;
    const adj = {};
    graphData.links.forEach((l) => {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      (adj[s] = adj[s] || new Set()).add(t);
      (adj[t] = adj[t] || new Set()).add(s);
    });
    const n1 = new Set(adj[selectedNode.id] || []);
    const n2 = new Set();
    if (focusMode >= 2) n1.forEach((id) => (adj[id] || new Set()).forEach((x) => {
      if (x !== selectedNode.id && !n1.has(x)) n2.add(x);
    }));
    return { n1, n2 };
  }, [selectedNode, focusMode, graphData]);

  const nodeColor = useCallback((n) => {
    if (colorBy === "party") return PARTY_COLOR[n.party] || "#9ca3af";
    return n.community == null ? "#6b7280" : COMMUNITY_PALETTE[n.community % COMMUNITY_PALETTE.length];
  }, [colorBy]);

  const nodeSize = useCallback((n) => 2.5 + (n.percentiles?.[sizeBy] || 0) / 14, [sizeBy]);

  const drawNode = useCallback((node, ctx, scale) => {
    const r = nodeSize(node);
    let alpha = 1;
    if (highlight) {
      alpha = highlight.hits.has(node.id) ? 1 : highlight.neigh.has(node.id) ? 0.8 : 0.08;
    } else if (selectedNode && focusSets) {
      alpha = node.id === selectedNode.id ? 1
        : focusSets.n1.has(node.id) ? 0.9
        : focusSets.n2.has(node.id) ? 0.55
        : 0.04;
    } else if (selectedNode) {
      const allies = new Set((selectedNode.topAllies || []).map((a) => a.id));
      alpha = node.id === selectedNode.id ? 1 : allies.has(node.id) ? 0.85 : 0.15;
    }
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = nodeColor(node);
    ctx.fill();
    if ((highlight && highlight.hits.has(node.id)) || (selectedNode && node.id === selectedNode.id)) {
      ctx.lineWidth = 1.5 / scale;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
    }
    if (scale > 2.2 || (highlight && highlight.hits.has(node.id))) {
      ctx.font = `${Math.max(3.2, 10 / scale)}px Inter, sans-serif`;
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.textAlign = "center";
      ctx.fillText(node.name.split(",")[0], node.x, node.y - r - 2);
    }
    ctx.globalAlpha = 1;
  }, [highlight, selectedNode, focusSets, nodeColor, nodeSize]);

  // cluster labels at community centroids (community coloring only)
  const drawClusterLabels = useCallback((ctx, scale) => {
    if (colorBy !== "community" || !data) return;
    const centers = {};
    graphData.nodes.forEach((n) => {
      if (n.community == null || n.x == null) return;
      const c = (centers[n.community] = centers[n.community] || { x: 0, y: 0, n: 0 });
      c.x += n.x; c.y += n.y; c.n += 1;
    });
    const top = (data.communities || []).slice(0, 8);
    ctx.textAlign = "center";
    top.forEach((cm) => {
      const c = centers[cm.id];
      if (!c || c.n < 3) return;
      ctx.font = `700 ${Math.max(4, 12 / scale)}px Inter, sans-serif`;
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText(cm.label, c.x / c.n, c.y / c.n);
    });
  }, [colorBy, data, graphData]);

  const onLinkClick = useCallback(async (l) => {
    const a = typeof l.source === "object" ? l.source.id : l.source;
    const b = typeof l.target === "object" ? l.target.id : l.target;
    setSelectedNode(null);
    setEdgeLoading(true);
    setEdgeDetail({ pending: true });
    try {
      const r = await axios.get(`${API}/network/edge`, { params: { a, b, signal, congress } });
      setEdgeDetail(r.data);
    } catch {
      setEdgeDetail({ error: true });
    } finally {
      setEdgeLoading(false);
    }
  }, [signal, congress]);

  const tooltip = useCallback((n) => {
    const trait =
      (n.percentiles?.betweenness ?? 0) >= 90 ? "Bridge between groups" :
      (n.percentiles?.eigenvector ?? 0) >= 90 ? "High quiet influence" :
      (n.percentiles?.degree ?? 0) >= 90 ? "Among the most connected" :
      (n.percentiles?.degree ?? 0) <= 10 ? "Few strong ties here" : n.communityLabel;
    return `<div style="background:#141924;border:1px solid #2a3348;border-radius:8px;padding:8px 10px;font-size:12px;color:#e6e9f0;">
      <b>${n.name}</b> <span style="color:${PARTY_COLOR[n.party] || "#aaa"}">(${n.party}-${n.state})</span><br/>
      <span style="color:#f97316">${trait}</span></div>`;
  }, []);

  const leaderboard = useMemo(() => {
    if (!data) return [];
    return [...data.nodes].sort((x, y) => (y[sizeBy] || 0) - (x[sizeBy] || 0)).slice(0, 8);
  }, [data, sizeBy]);

  const metric = SIZE_METRICS.find((m) => m.id === sizeBy);
  const crossCommunities = (data?.communities || []).filter((c) => c.crossParty);

  return (
    <div className="min-h-screen bg-[#0b0e14]">
      <Navigation />
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 py-8" ref={pageWrapRef}>
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="p-2 rounded-xl bg-orange-500/15 border border-orange-500/30" data-testid="backroom-logo">
                <Network className="w-7 h-7 text-orange-400" />
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="network-title">WHIP Backroom</h1>
            </div>
            <p className="text-white/50 mt-1 text-sm max-w-2xl">
              Who actually works with whom. Pick a connection signal, then click any member or line for a plain-English breakdown.
            </p>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="view-tabs">
          {VIEWS.map((v) => (
            <button key={v.id} data-testid={`view-${v.id}`} onClick={() => { setView(v.id); setSelectedNode(null); setEdgeDetail(null); }}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-2 ${view === v.id ? "bg-orange-500/15 border-orange-500/50 text-orange-300" : "border-white/10 text-white/55 hover:text-white"}`}>
              <v.icon className="w-4 h-4" /> {v.label}
            </button>
          ))}
          {view !== "network" && (
            <div className="ml-auto flex items-center gap-2">
              {["", "Senate", "House"].map((c) => (
                <button key={c || "all"} data-testid={`alt-chamber-${c || "all"}`} onClick={() => setChamber(c)}
                  disabled={view === "heatmap" && c === ""}
                  className={`px-3 py-1.5 rounded-lg text-sm border disabled:opacity-30 ${chamber === c ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-white/55 hover:text-white"}`}>
                  {c || "Both"}
                </button>
              ))}
            </div>
          )}
          <div className={`relative ${view === "network" ? "ml-auto" : ""}`}>
            <button data-testid="saved-views-btn" onClick={() => setShowViews((s) => !s)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${showViews ? "bg-orange-500/15 border-orange-500/50 text-orange-300" : "border-white/10 text-white/55 hover:text-white"}`}>
              <Bookmark className="w-4 h-4" /> Saved views{savedViews.length > 0 ? ` (${savedViews.length})` : ""}
            </button>
            {showViews && (
              <div data-testid="saved-views-panel" className="absolute right-0 top-full mt-2 z-30 w-80 rounded-xl border border-white/15 bg-[#141924] shadow-2xl p-3">
                <div className="flex items-center gap-2 mb-3">
                  <input data-testid="save-view-name" value={viewName} onChange={(e) => setViewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveCurrentView()}
                    placeholder="Name this setup…" maxLength={60}
                    className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white focus:border-orange-500/50 outline-none" />
                  <button data-testid="save-view-submit" onClick={saveCurrentView} disabled={savingView || !viewName.trim()}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-orange-500/15 border border-orange-500/40 text-orange-300 hover:bg-orange-500/25 disabled:opacity-40 transition-colors">
                    {savingView ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </button>
                </div>
                {viewError && <p className="text-red-400/90 text-xs px-1 pb-2" data-testid="save-view-error">{viewError}</p>}
                {savedViews.length === 0 ? (
                  <p className="text-white/40 text-xs px-1 pb-1" data-testid="saved-views-empty">No saved views yet, set up the map how you like it, then save it here.</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {savedViews.map((v) => (
                      <div key={v.view_id} className="flex items-center gap-1">
                        <button data-testid={`saved-view-item-${v.view_id}`} onClick={() => applySavedView(v.config || {})}
                          className="flex-1 min-w-0 text-left px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                          <span className="block text-sm text-white/80 truncate">{v.name}</span>
                          <span className="block text-[11px] text-white/35 truncate">
                            {(VIEWS.find((x) => x.id === (v.config?.view || "network"))?.label) || "Network"}
                            {" · "}{SIGNALS.find((s) => s.id === v.config?.signal)?.label || "Voting alignment"}
                            {v.config?.chamber ? ` · ${v.config.chamber}` : ""}
                          </span>
                        </button>
                        <button data-testid={`delete-view-${v.view_id}`} onClick={() => deleteSavedView(v.view_id)} title="Delete saved view"
                          className="p-1.5 rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {view === "ideology" && <IdeologyMap congress={congress} chamber={chamber} dims={dims} />}
        {view === "heatmap" && (
          <HeatmapView congress={congress} chamber={chamber || "Senate"}
            sidePanel={edgeDetail ? <EdgePanel detail={edgeDetail} loading={edgeLoading} onClose={() => setEdgeDetail(null)} /> : null}
            onPair={async (a, b) => {
              setEdgeLoading(true);
              setEdgeDetail({ pending: true });
              try {
                const r = await axios.get(`${API}/network/edge`, { params: { a, b, signal: "votes", congress } });
                setEdgeDetail(r.data);
              } catch { setEdgeDetail({ error: true }); }
              finally { setEdgeLoading(false); }
            }} />
        )}
        {view === "compare" && <CompareView chamber={chamber} dims={dims} />}

        {view === "network" && (
        <>
        {/* Controls */}
        <div className="whip-surface rounded-xl border border-white/10 p-4 mb-4 space-y-3" data-testid="network-controls">
          <div className="flex flex-wrap items-center gap-2">
            {SIGNALS.map((s) => (
              <button key={s.id} data-testid={`signal-${s.id}`} onClick={() => setSignal(s.id)} title={s.blurb}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${signal === s.id ? "bg-orange-500/15 border-orange-500/50 text-orange-300" : "border-white/10 text-white/60 hover:text-white"}`}>
                <s.icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            ))}
            <div className="h-5 w-px bg-white/10" />
            {["", "Senate", "House"].map((c) => (
              <button key={c || "all"} data-testid={`network-chamber-${c || "all"}`} onClick={() => setChamber(c)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${chamber === c ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-white/55 hover:text-white"}`}>
                {c || "Both"}
              </button>
            ))}
            <div className="h-5 w-px bg-white/10" />
            <select data-testid="network-congress" value={congress} onChange={(e) => setCongress(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white/80">
              <option value={119}>119th (live)</option>
              <option value={118}>118th</option>
            </select>
            {signal !== "committee" && (
              <select data-testid="issue-lens" value={policyArea}
                onChange={(e) => { setPolicyArea(e.target.value); if (e.target.value && signal === "votes") setSignal("cosponsor"); }}
                title="Issue lens, rebuild the network using only bills in one policy area (uses co-sponsorship activity)"
                className={`border rounded-lg px-2.5 py-1.5 text-sm max-w-[160px] ${policyArea ? "bg-orange-500/10 border-orange-500/40 text-orange-300" : "bg-white/5 border-white/10 text-white/80"}`}>
                <option value="">All issues</option>
                {policyAreas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            <button data-testid="briefing-btn" onClick={fetchBriefing} disabled={briefingLoading || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 transition-colors disabled:opacity-50">
              {briefingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Briefing
            </button>
            <div className="ml-auto relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-white/40" />
              <input data-testid="network-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Find a legislator…"
                className="bg-white/5 border border-white/10 rounded-lg pl-8 pr-8 py-1.5 text-sm text-white w-44 focus:border-orange-500/50 outline-none" />
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-2 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-white/40 text-xs uppercase tracking-wider">Color</span>
            {["party", "community"].map((c) => (
              <button key={c} data-testid={`color-${c}`} onClick={() => setColorBy(c)}
                className={`px-2.5 py-1 rounded-md text-xs border ${colorBy === c ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-white/50"}`}>
                {c === "party" ? "Party" : "Community"}
              </button>
            ))}
            <span className="text-white/40 text-xs uppercase tracking-wider ml-2">Size</span>
            {SIZE_METRICS.map((m) => (
              <button key={m.id} data-testid={`size-${m.id}`} onClick={() => setSizeBy(m.id)} title={m.blurb}
                className={`px-2.5 py-1 rounded-md text-xs border ${sizeBy === m.id ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "border-white/10 text-white/50"}`}>
                {m.label}
              </button>
            ))}
            <span className="text-white/40 text-xs uppercase tracking-wider ml-2">Party</span>
            {["all", "D", "R", "I"].map((p) => (
              <button key={p} data-testid={`party-filter-${p}`} onClick={() => setPartyFilter(p)}
                className={`px-2.5 py-1 rounded-md text-xs border ${partyFilter === p ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-white/50"}`}
                style={p !== "all" && partyFilter === p ? { color: PARTY_COLOR[p] } : {}}>
                {p === "all" ? "All" : p}
              </button>
            ))}
            <select data-testid="state-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white/70">
              <option value="all">All states</option>
              {allStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="committee-filter" value={committeeFilter} onChange={(e) => setCommitteeFilter(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white/70 max-w-[170px]">
              <option value="all">All committees</option>
              {allCommittees.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {data && (
              <label className="flex items-center gap-2 ml-auto text-xs text-white/50">
                Min tie strength
                <input data-testid="min-weight" type="range"
                  min={data.meta?.signal === "votes" ? 70 : 1}
                  max={data.meta?.signal === "votes" ? 98 : 12}
                  step={1} value={minWeight ?? 0}
                  onChange={(e) => setMinWeight(Number(e.target.value))}
                  className="accent-orange-500 w-32" />
                <span className="text-orange-300 font-mono w-8">{minWeight}{data.meta?.signal === "votes" ? "%" : ""}</span>
              </label>
            )}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Graph */}
          <div className="col-span-12 lg:col-span-8">
            <div ref={wrapRef} className="relative whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid="network-graph">
              {loading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b0e14]/70">
                  <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                </div>
              )}
              {building && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0b0e14]/85 px-8 text-center" data-testid="network-building">
                  <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                  <p className="text-white/80 text-sm max-w-md">{building.message}</p>
                  {building.progress?.total > 0 && (
                    <p className="text-white/40 text-xs font-mono">{building.progress.done} / {building.progress.total} bills fetched</p>
                  )}
                </div>
              )}
              {error && <div className="absolute inset-0 z-10 flex items-center justify-center text-white/60 text-sm">{error}</div>}
              {!loading && !building && !error && data && data.nodes?.length === 0 && (
                <div className="absolute inset-0 z-10 flex items-center justify-center px-8 text-center" data-testid="network-empty">
                  <p className="text-white/55 text-sm max-w-md">No network at these settings. {policyArea ? "The issue lens works best with the Co-sponsorship signal, or clear the lens." : "Try lowering the minimum tie strength or switching signals."}</p>
                </div>
              )}
              <ForceGraph2D
                ref={graphRef}
                width={dims.w}
                height={dims.h}
                graphData={graphData}
                backgroundColor="#0b0e14"
                nodeCanvasObject={drawNode}
                nodePointerAreaPaint={(node, color, ctx) => {
                  ctx.fillStyle = color;
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, nodeSize(node) + 2, 0, 2 * Math.PI);
                  ctx.fill();
                }}
                nodeLabel={tooltip}
                onNodeClick={(n) => { setEdgeDetail(null); setSelectedNode(n); }}
                onBackgroundClick={() => { setSelectedNode(null); setEdgeDetail(null); setFocusMode(0); }}
                linkColor={(l) => {
                  if (selectedNode && focusSets) {
                    const s = typeof l.source === "object" ? l.source.id : l.source;
                    const t = typeof l.target === "object" ? l.target.id : l.target;
                    const touchesSel = s === selectedNode.id || t === selectedNode.id;
                    const inRing = (focusSets.n1.has(s) || focusSets.n2.has(s) || s === selectedNode.id) &&
                                   (focusSets.n1.has(t) || focusSets.n2.has(t) || t === selectedNode.id);
                    if (touchesSel) return l.crossParty ? "rgba(234,179,8,0.85)" : "rgba(249,115,22,0.6)";
                    if (focusMode >= 2 && inRing) return "rgba(255,255,255,0.10)";
                    return "rgba(255,255,255,0.02)";
                  }
                  return l.crossParty ? "rgba(234,179,8,0.45)" : "rgba(255,255,255,0.12)";
                }}
                linkWidth={(l) => (l.crossParty ? 1.4 : 0.6)}
                linkHoverPrecision={6}
                onLinkClick={onLinkClick}
                onRenderFramePost={drawClusterLabels}
                cooldownTicks={120}
                onEngineStop={() => { if (graphRef.current && !selectedNode && !search) graphRef.current.zoomToFit(500, 60); }}
                enableNodeDrag={true}
              />
              {/* View controls */}
              <div className="absolute top-3 left-3 z-20 flex flex-col gap-1.5" data-testid="graph-view-controls">
                <button data-testid="graph-fit" title="Re-center the graph"
                  onClick={() => graphRef.current && graphRef.current.zoomToFit(500, 60)}
                  className="p-2 rounded-lg bg-[#10141d]/90 border border-white/15 text-white/70 hover:text-orange-300 hover:border-orange-500/40 transition-colors">
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button data-testid="graph-zoom-in" title="Zoom in"
                  onClick={() => graphRef.current && graphRef.current.zoom(graphRef.current.zoom() * 1.5, 300)}
                  className="p-2 rounded-lg bg-[#10141d]/90 border border-white/15 text-white/70 hover:text-orange-300 hover:border-orange-500/40 transition-colors">
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button data-testid="graph-zoom-out" title="Zoom out"
                  onClick={() => graphRef.current && graphRef.current.zoom(graphRef.current.zoom() / 1.5, 300)}
                  className="p-2 rounded-lg bg-[#10141d]/90 border border-white/15 text-white/70 hover:text-orange-300 hover:border-orange-500/40 transition-colors">
                  <ZoomOut className="w-4 h-4" />
                </button>
                {selectedNode && (
                  <div className="mt-2 bg-[#10141d]/95 border border-orange-500/30 rounded-lg p-1.5 space-y-1" data-testid="focus-controls">
                    <p className="text-[9px] font-mono uppercase tracking-wider text-orange-400/80 px-1">Focus</p>
                    {[{ m: 0, l: "Off", t: "Show the full network" },
                      { m: 1, l: "Ties", t: "Highlight only members directly connected to the selected node" },
                      { m: 2, l: "+2°", t: "Also highlight the members connected to those connections" }].map(({ m, l, t }) => (
                      <button key={m} data-testid={`focus-${m}`} title={t} onClick={() => setFocusMode(m)}
                        className={`block w-full px-2 py-1 rounded text-[11px] font-medium border transition-colors ${focusMode === m ? "bg-orange-500/20 border-orange-500/50 text-orange-300" : "border-white/10 text-white/55 hover:text-white"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Legend */}
              <div className="absolute bottom-3 left-3 bg-[#10141d]/90 border border-white/10 rounded-lg px-3 py-2 text-[11px] text-white/60 space-y-1" data-testid="network-legend">
                {colorBy === "party" ? (
                  <div className="flex gap-3">
                    {Object.entries(PARTY_COLOR).map(([p, c]) => (
                      <span key={p} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} /> {PARTY_NAME[p]}</span>
                    ))}
                  </div>
                ) : (
                  <span>Colors = detected communities · labels on the map</span>
                )}
                <div className="flex gap-3">
                  <span className="flex items-center gap-1"><span className="w-4 h-0.5 inline-block bg-yellow-500/60" /> cross-party tie</span>
                  <span>Node size = {metric?.label.toLowerCase()}</span>
                </div>
                <div className="text-white/35">Scroll to zoom · drag nodes · click a line for what drives it</div>
              </div>
              {data && (
                <div className="absolute top-3 right-3 bg-[#10141d]/90 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] font-mono text-white/55" data-testid="network-meta">
                  {graphData.nodes.length} members · {graphData.links.length} ties · {data.meta?.crossPartyEdges} cross-party
                  {data.meta?.signal === "votes" && data.meta?.rollCalls != null && (
                    <span className="block text-white/40 mt-0.5" data-testid="network-coverage">
                      Built from {data.meta.rollCalls} recorded roll calls on {data.meta.rollCallBills} tracked bills, not every vote taken this Congress
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Side panel */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            {briefing && (
              <div className="whip-surface rounded-xl border border-orange-500/40 p-4" data-testid="briefing-panel">
                <div className="flex items-start gap-2 mb-3">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-orange-400" /> Strategist's Briefing
                  </h3>
                  <button onClick={() => setBriefing(null)} data-testid="briefing-close" className="ml-auto text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-3">
                  {briefing.map((f, i) => (
                    <div key={f.title || i} data-testid={`briefing-finding-${i}`}>
                      <p className="text-sm font-semibold text-orange-300">{i + 1}. {f.title}</p>
                      <p className="text-[13px] text-white/70 leading-relaxed mt-0.5">{f.detail}</p>
                    </div>
                  ))}
                </div>
                {policyArea && <p className="text-[10px] text-white/35 mt-3">Lens: {policyArea}</p>}
              </div>
            )}
            {selectedNode ? (
              <NodePanel node={selectedNode} signal={signal} congress={congress} chamber={chamber} onClose={() => setSelectedNode(null)}
                onSelectAlly={(id) => { const n = data?.nodes.find((x) => x.id === id); if (n) setSelectedNode(n); }} />
            ) : edgeDetail ? (
              <EdgePanel detail={edgeDetail} loading={edgeLoading} onClose={() => setEdgeDetail(null)} />
            ) : (
              <>
                <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="network-communities">
                  <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Users className="w-4 h-4 text-orange-400" /> Detected communities</h3>
                  <p className="text-[11px] text-white/40 mb-3">Groups whose members are more connected to each other than to everyone else.</p>
                  <div className="space-y-2 max-h-[290px] overflow-y-auto pr-1">
                    {(data?.communities || []).slice(0, 10).map((c) => (
                      <div key={c.id} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`community-${c.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COMMUNITY_PALETTE[c.id % COMMUNITY_PALETTE.length] }} />
                          <span className="text-sm text-white font-medium">{c.label}</span>
                          <span className="text-[10px] text-white/40 ml-auto">{c.size} members</span>
                          {c.crossParty && <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">CROSS-PARTY</span>}
                        </div>
                        <p className="text-[11px] text-white/45 mt-1">{c.summary}</p>
                      </div>
                    ))}
                  </div>
                  {crossCommunities.length > 0 && (
                    <p className="text-[11px] text-yellow-300/80 mt-3 flex items-start gap-1.5">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      {crossCommunities.length} group{crossCommunities.length !== 1 ? "s" : ""} cut across party lines, the most notable finding in this view.
                    </p>
                  )}
                </div>
                <div className="whip-surface rounded-xl border border-white/10 p-4" data-testid="network-leaderboard">
                  <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-orange-400" /> Top by {metric?.label.toLowerCase()}</h3>
                  <p className="text-[11px] text-white/40 mb-3">{metric?.blurb}.</p>
                  <div className="space-y-1">
                    {leaderboard.map((n, i) => (
                      <button key={n.id} onClick={() => setSelectedNode(n)} data-testid={`leaderboard-${i}`}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 text-left">
                        <span className="text-[10px] font-mono text-white/35 w-4">{i + 1}</span>
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PARTY_COLOR[n.party] || "#9ca3af" }} />
                        <span className="text-sm text-white/85 truncate">{n.name}</span>
                        <span className="text-[10px] text-white/40 ml-auto shrink-0">{n.state}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

const Pct = ({ label, value, blurb }) => (
  <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2" title={blurb}>
    <p className="text-lg font-bold text-orange-300">{Math.round(value)}<span className="text-[10px] text-white/40">th pct</span></p>
    <p className="text-[10px] text-white/50 uppercase tracking-wider">{label}</p>
  </div>
);

const NodePanel = ({ node, signal, congress, chamber, onClose, onSelectAlly }) => {
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setInsight(null);
    setInsightLoading(true);
    const params = { id: node.id, signal, congress };
    if (chamber) params.chamber = chamber;
    axios.get(`${API}/network/insight`, { params })
      .then((r) => { if (live) setInsight(r.data?.insight || null); })
      .catch(() => {})
      .finally(() => { if (live) setInsightLoading(false); });
    return () => { live = false; };
  }, [node.id, signal, congress, chamber]);

  return (
  <div className="whip-surface rounded-xl border border-orange-500/30 p-4" data-testid="node-panel">
    <div className="flex items-start gap-2">
      <div>
        <h3 className="text-lg font-bold text-white leading-tight">{node.name}</h3>
        <p className="text-xs mt-0.5" style={{ color: PARTY_COLOR[node.party] || "#aaa" }}>
          {PARTY_NAME[node.party] || node.party} · {node.state} · {node.chamber}
        </p>
      </div>
      <button onClick={onClose} data-testid="node-panel-close" className="ml-auto text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
    <div className="mt-3 rounded-lg bg-orange-500/[0.06] border border-orange-500/20 px-3 py-2.5" data-testid="node-insight">
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400/90 mb-1.5 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Network Context
      </p>
      {insightLoading ? (
        <div className="flex items-center gap-2 text-white/45 text-xs py-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading their network…</div>
      ) : (
        <p className="text-sm text-white/80 leading-relaxed" data-testid="node-summary">{insight || node.summary}</p>
      )}
    </div>
    <div className="grid grid-cols-3 gap-2 mt-4">
      <Pct label="Connections" value={node.percentiles?.degree || 0} blurb="Share of members with fewer strong ties than them" />
      <Pct label="Bridge" value={node.percentiles?.betweenness || 0} blurb="How often shortest paths between groups pass through them" />
      <Pct label="Influence" value={node.percentiles?.eigenvector || 0} blurb="Connected to other well-connected members" />
    </div>
    <p className="text-[10px] text-white/35 mt-1.5">Percentile within this view, 90th = higher than 90% of members shown.</p>
    {(node.topAllies || []).length > 0 && (
      <div className="mt-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/45 mb-1.5">Strongest ties</p>
        <div className="space-y-1">
          {node.topAllies.map((a) => (
            <button key={a.id} onClick={() => onSelectAlly(a.id)} data-testid={`ally-${a.id}`}
              className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 text-left">
              <span className="w-2 h-2 rounded-full" style={{ background: PARTY_COLOR[a.party] || "#9ca3af" }} />
              <span className="text-sm text-white/80">{a.name}</span>
              <span className="text-[10px] font-mono text-orange-300/80 ml-auto">{a.weight}</span>
            </button>
          ))}
        </div>
      </div>
    )}
    {(node.committees || []).length > 0 && (
      <div className="mt-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/45 mb-1.5">Committees</p>
        <ul className="space-y-0.5">
          {node.committees.slice(0, 5).map((c) => <li key={c} className="text-[12px] text-white/60">{c}</li>)}
        </ul>
      </div>
    )}
    <Link to={`/legislator/${node.legislatorId}`} data-testid="node-full-profile"
      className="mt-4 inline-flex items-center gap-1 text-sm text-orange-300 hover:text-orange-200">
      Full profile <ArrowUpRight className="w-3.5 h-3.5" />
    </Link>
  </div>
  );
};

const EdgePanel = ({ detail, loading, onClose }) => (
  <div className="whip-surface rounded-xl border border-yellow-500/30 p-4" data-testid="edge-panel">
    <div className="flex items-start gap-2 mb-2">
      <h3 className="text-sm font-bold text-white flex items-center gap-2"><Link2 className="w-4 h-4 text-yellow-400" /> What drives this connection</h3>
      <button onClick={onClose} data-testid="edge-panel-close" className="ml-auto text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
    {loading || detail.pending ? (
      <div className="flex items-center gap-2 text-white/50 text-sm py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</div>
    ) : detail.error ? (
      <p className="text-white/50 text-sm">Could not load the connection detail, try another line.</p>
    ) : (
      <>
        <p className="text-sm text-white/85">{detail.a?.name} <span className="text-white/40">×</span> {detail.b?.name}</p>
        <p className="text-sm text-white/70 mt-2 leading-relaxed" data-testid="edge-explanation">{detail.explanation}</p>
        {detail.agreementPercent != null && (
          <p className="text-2xl font-bold text-yellow-300 mt-3">{detail.agreementPercent}% <span className="text-xs text-white/45 font-normal">agreement over {detail.commonVotes} shared votes</span></p>
        )}
        {(detail.sharedVotes || []).length > 0 && (
          <div className="mt-3 space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {detail.sharedVotes.map((v, i) => (
              <div key={`${v.title}-${v.date || i}`} className="rounded-md bg-white/[0.03] border border-white/10 px-2.5 py-1.5">
                <p className="text-[12px] text-white/75 truncate">{v.title}</p>
                <p className={`text-[10px] mt-0.5 ${v.agreed ? "text-emerald-300/80" : "text-rose-300/80"}`}>
                  {v.agreed ? "Voted the same way" : `Split, ${v.aVote} vs ${v.bVote}`} · {v.date?.slice(0, 10)}
                </p>
              </div>
            ))}
          </div>
        )}
        {(detail.sharedBills || []).length > 0 && (
          <div className="mt-3 space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {detail.sharedBills.map((b, i) => (
              <div key={b.billId || b.id || `${b.title}-${i}`} className="rounded-md bg-white/[0.03] border border-white/10 px-2.5 py-1.5">
                <p className="text-[12px] text-white/75">{b.title}</p>
                {b.subjects?.length > 0 && <p className="text-[10px] text-white/40 mt-0.5">{b.subjects.join(" · ")}</p>}
              </div>
            ))}
          </div>
        )}
        {(detail.sharedCommittees || []).length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-mono uppercase tracking-wider text-white/45 mb-1">Shared committees</p>
            <ul className="space-y-0.5">{detail.sharedCommittees.map((c) => <li key={c} className="text-[12px] text-white/60">{c}</li>)}</ul>
          </div>
        )}
      </>
    )}
  </div>
);
